import { Component, OnDestroy } from '@angular/core';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import printJS from 'print-js';
import { firstValueFrom, Observable, Subscription } from 'rxjs';

import { Bill } from '../../../../../../../../data/dietitian/domain/bill/bill';
import { BillParams } from '../../../../../../../../data/dietitian/domain/bill/bill-params';
import { Dietitian } from '../../../../../../../../data/dietitian/domain/dietitian';
import { ConfirmationModalComponent } from '../../../../../../../components/confirmation-modal/confirmation-modal.component';
import { EditBillModalComponent } from '../../../../../../../components/edit-bill-modal/edit-bill-modal.component';
import { PeriodBarResult } from '../../../../../../../components/period-bar/period-bar.component';
import { AccountInformationsViewModel } from '../../../../../account/routes/account-informations/account-informations.facade';
import { BillFilter, BillingFacade } from '../../../../billing.facade';
import { BillingService } from '../../../../billing.service';
import { EmailModalComponent } from '../../../../components/email-modal/email-modal.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-bills',
  templateUrl: './bills.component.html',
  styleUrls: ['./bills.component.scss'],
})
export class BillsComponent implements OnDestroy {
  public dietitian?: Dietitian;
  public isLoading = false;
  public isAppendLoading = false;
  public isOnError = false;
  public billList: Bill[] | undefined;
  public billFilter: BillFilter = { period: undefined, status: undefined };
  public totalExcl = '-';
  public totalIncl = '-';
  public count = 0;
  public billParams: BillParams;
  private currentPage = 0;
  private accountStatus$: Observable<AccountInformationsViewModel>;
  private accountStatusSubscription: Subscription;
  private billEventSubscription: Subscription;
  private hasBill = false;

  constructor(
    public facade: BillingFacade,
    public billingService: BillingService,
    private modalService: BsModalService,
    private toastr: ToastrService,
  ) {
    this.isLoading = false;
    this.accountStatus$ = this.facade.accountStatus$;
    this.accountStatusSubscription = this.accountStatus$.subscribe(
      async (state) => {
        this.dietitian = state.dietitian;
        this.isLoading = true;
        this.billParams = await this.facade.loadBillParams(
          this.dietitian.dietitianId.id.toString(),
        );
        this.isLoading = false;
        this.loadPage(0);
      },
    );
    this.billEventSubscription = this.facade.billEventProvider$.subscribe(
      () => {
        this.loadPage(0);
      },
    );
  }

  ngOnDestroy(): void {
    this.accountStatusSubscription?.unsubscribe();
    this.billEventSubscription?.unsubscribe();
  }

  onScroll() {
    this.loadPage(this.currentPage + 1);
  }

  loadPage(page: number) {
    if (!this.dietitian) {
      return;
    }
    if (page === 0) {
      this.isLoading = true;
      this.billList = [];
    } else {
      this.isAppendLoading = true;
    }
    this.facade
      .findAllArchived(
        this.dietitian.id.toString(),
        this.billFilter,
        this.billList && this.billList.length > 0
          ? this.billList[this.billList.length - 1].id.toString()
          : undefined,
      )
      .then((bills) => {
        bills.forEach((b) => this.billList?.push(b));
        this.currentPage = page;
        this.isLoading = false;
        this.isAppendLoading = false;
        this.isOnError = false;
      })
      .catch(() => {
        this.isOnError = true;
      });

    this.facade.calcTotBill(true, this.billFilter).then((res) => {
      this.count = res.count;
      this.totalExcl = this.facade.format2Decimals(res.amountExcludingTax);
      this.totalIncl = this.facade.format2Decimals(res.amountIncludingTax);
    });
  }

  async view(bill: Bill) {
    console.log('👀 view bill', { id: bill.id.toString() });
    if (!this.hasBill) {
      this.hasBill = true;
      const mode: 'create' | 'edit' | 'view' | undefined = 'view';
      const initialState = {
        class: 'modal-lg',
        initialState: {
          target: bill.patientId
            ? await this.facade.loadPatient(bill.patientId.id.toString())
            : bill.thirdParty,
          dietitian: this.dietitian,
          bill,
          mode,
          billParams: this.billParams,
        },
      };
      this.modalService.show(EditBillModalComponent, initialState);
      this.hasBill = false;
    }
  }

  async email(bill: Bill) {
    let email;
    if (bill.patientId) {
      const patient = await this.facade.loadPatient(
        bill.patientId?.id.toString(),
      );
      email = patient.email;
    } else if (bill.thirdParty) {
      email = bill.thirdParty.email;
    }
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      backdrop: 'static',
      initialState: {
        email,
      },
    };
    const modal = this.modalService.show(EmailModalComponent, initialState);
    if (modal.content instanceof EmailModalComponent) {
      firstValueFrom(modal.content.onValidate).then((email) => {
        this.facade.email(this.dietitian?.email, email, bill);
      });
    }
  }

  async share(bill: Bill) {
    if (this.dietitian) {
      this.facade
        .share(
          this.dietitian,
          await this.facade.loadPatient(bill.patientId?.id.toString()),
          bill,
        )
        .then();
    }
  }

  restore(bill: Bill) {
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: 'Restaurer cette facture ?',
        subtitle:
          'La facture sera déplacée dans l\'onglet "Facturation courante". L\'action est réversible.',
      },
    };
    const modal = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );
    if (modal.content instanceof ConfirmationModalComponent) {
      const subscription = modal.content.validate.subscribe(() => {
        this.facade.restore(bill).then(() => {
          this.loadPage(0);
          this.billingService.countRefresh.next(bill);
        });
        subscription?.unsubscribe();
      });
    }
  }

  async download(bill: Bill) {
    if (this.dietitian) {
      const loadedBill = await this.facade.load(
        this.dietitian.id.toString(),
        bill.id.toString(),
      );
      const blob: Blob | undefined = await new Promise((resolve) => {
        if (!loadedBill.url) {
          return resolve(undefined);
        }

        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function () {
          resolve(xhr.response);
        };
        xhr.onerror = function () {
          resolve(undefined);
        };
        xhr.open('GET', loadedBill.url);
        xhr.send();
      });

      if (!blob || blob.size <= 0) {
        this.toastr.error(
          "La facture PDF n'est pas encore générée. essayez à nouveau dans quelques instants.",
        );
        return;
      }

      let downloadButton = document.createElement('a');
      downloadButton.href = window.URL.createObjectURL(blob);
      downloadButton.setAttribute(
        'download',
        'facture_' +
          loadedBill.referencePrefix +
          '-' +
          loadedBill.reference?.toString().padStart(4, '0'),
      );
      downloadButton = document.body.appendChild(downloadButton);
      downloadButton.click();
      document.body.removeChild(downloadButton);
    } else {
      this.toastr.error('Erreur, veuillez essayer à nouveau ultérieurement.');
    }
  }

  async print(bill: Bill) {
    if (this.dietitian) {
      const loadedBill = await this.facade.load(
        this.dietitian.id.toString(),
        bill.id.toString(),
      );
      const blob: Blob | undefined = await new Promise((resolve) => {
        if (!loadedBill.url) {
          return resolve(undefined);
        }

        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.onload = function () {
          resolve(xhr.response);
        };
        xhr.onerror = function () {
          resolve(undefined);
        };
        xhr.open('GET', loadedBill.url);
        xhr.send();
      });

      if (!blob || blob.size <= 0) {
        this.toastr.error(
          "La facture PDF n'est pas encore générée. essayez à nouveau dans quelques instants.",
        );
        return;
      }
      const url: string = window.URL.createObjectURL(blob);
      printJS(url, 'pdf');
    } else {
      this.toastr.error('Erreur, veuillez essayer à nouveau ultérieurement.');
    }
  }

  onPeriodFilter(periodBarResult: PeriodBarResult) {
    this.billFilter.period = periodBarResult;
    this.loadPage(0);
  }

  countBill() {
    if (!this.isLoading) {
      return (
        this.count + ' facture' + (this.count && this.count > 1 ? 's' : '')
      );
    }
    return '';
  }

  totalExclBill() {
    if (!this.isLoading) {
      return this.totalExcl;
    }
    return '';
  }

  totalInclBill() {
    if (!this.isLoading) {
      return this.totalIncl;
    }
    return '';
  }

  async export() {
    if (this.dietitian) {
      const bills = await this.facade.findAllExported(
        this.dietitian.id.toString(),
        this.billFilter,
        true,
      );
      const csv = await this.billingService.exportBill(
        bills,
        this.dietitian.id.toString(),
      );

      const a = document.createElement('a');
      a.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
      const d = new Date();
      a.setAttribute('download', 'facture_archive_' + d.toISOString() + '.csv');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }
}
